import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  Archive,
  Bell,
  ChevronDown,
  ContactRound,
  FileText,
  Folder,
  Gauge,
  Inbox,
  Mail,
  MailCheck,
  Menu,
  Plus,
  Search,
  Send,
  Settings,
  ShieldCheck,
  ShieldAlert,
  Trash2,
  UserRoundCog,
  UsersRound
} from "lucide-react";
import type {
  Dashboard,
  EmailAccount,
  EmailContact,
  EmailSummary,
  EmailTemplate,
  MailFolder,
  PageKey,
  ToastMessage,
  User
} from "./types";
import { api, session } from "./lib/api";
import { senderName } from "./lib/format";
import type { ComposerSeed } from "./components/Composer";
import Composer from "./components/Composer";
import { Badge, Button, IconButton, Modal, ToastStack } from "./components/ui";
import UserAvatar from "./components/UserAvatar";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import MailPage from "./pages/MailPage";
import TemplatesPage from "./pages/TemplatesPage";
import AccountsPage from "./pages/AccountsPage";
import TeamPage from "./pages/TeamPage";
import AuditPage from "./pages/AuditPage";
import SettingsPage from "./pages/SettingsPage";
import ContactsPage from "./pages/ContactsPage";

const WORKSPACE_POLL_MS = 10_000;

async function requestNotificationAccess() {
  if (window.desktop?.notify) return true;
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  return (await Notification.requestPermission()) === "granted";
}

async function showNewMailNotification(title: string, body: string) {
  if (window.desktop?.notify) {
    await window.desktop.notify(title, body);
    return;
  }
  if ("Notification" in window && Notification.permission === "granted")
    new Notification(title, { body });
  try {
    const audio = new AudioContext();
    const oscillator = audio.createOscillator();
    const gain = audio.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(740, audio.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1040, audio.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, audio.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, audio.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + 0.2);
    oscillator.connect(gain).connect(audio.destination);
    oscillator.start();
    oscillator.stop(audio.currentTime + 0.2);
    oscillator.addEventListener("ended", () => void audio.close());
  } catch {
    // Browser autoplay or operating-system policy may suppress the optional sound.
  }
}
const navGroups: Array<{
  label: string;
  adminOnly?: boolean;
  items: Array<{ page: PageKey; label: string; icon: typeof Inbox; adminOnly?: boolean }>;
}> = [
  {
    label: "Workspace",
    items: [
      { page: "dashboard", label: "Overview", icon: Gauge },
      { page: "inbox", label: "Inbox", icon: Inbox },
      { page: "sent", label: "Sent", icon: Send },
      { page: "drafts", label: "Drafts", icon: FileText },
      { page: "archive", label: "Archive", icon: Archive },
      { page: "spam", label: "Spam", icon: ShieldAlert },
      { page: "trash", label: "Trash", icon: Trash2 },
      { page: "contacts", label: "Contacts", icon: ContactRound },
      { page: "templates", label: "Templates", icon: FileText }
    ]
  },
  {
    label: "Administration",
    adminOnly: true,
    items: [
      { page: "accounts", label: "Email accounts", icon: MailCheck },
      { page: "team", label: "People", icon: UsersRound },
      { page: "roles", label: "Roles & access", icon: ShieldCheck },
      { page: "audit", label: "Audit activity", icon: Activity },
      { page: "settings", label: "Settings", icon: Settings }
    ]
  }
];

function PasswordChange({
  user,
  onChanged,
  notify
}: {
  user: User;
  onChanged: (user: User) => void;
  notify: (tone: "success" | "error" | "info", title: string, message?: string) => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (newPassword !== confirm) {
      notify("error", "Passwords don’t match");
      return;
    }
    setSaving(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      const updated = { ...user, mustChangePassword: false };
      onChanged(updated);
      notify("success", "Password changed", "Welcome to your workspace.");
    } catch (error) {
      notify("error", "Couldn’t change password", (error as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open
      onClose={() => undefined}
      title="Choose a secure password"
      subtitle="You need to replace your temporary password before entering the workspace."
    >
      <div className="stack-form">
        <label className="field">
          <span>Temporary password</span>
          <input
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoFocus
          />
        </label>
        <label className="field">
          <span>New password</span>
          <input
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </label>
        <label className="field">
          <span>Confirm new password</span>
          <input
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </label>
        <p className="password-rules">
          Use 8+ characters with uppercase, lowercase, a number, and a special character.
        </p>
      </div>
      <div className="modal-footer">
        <Button onClick={submit} disabled={saving}>
          {saving ? "Securing account…" : "Change password"}
        </Button>
      </div>
    </Modal>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [checkingSession, setCheckingSession] = useState(Boolean(session.token));
  const [page, setPage] = useState<PageKey>("dashboard");
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [mailFolders, setMailFolders] = useState<MailFolder[]>([]);
  const [customFolder, setCustomFolder] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerSeed, setComposerSeed] = useState<ComposerSeed>();
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [initialMessageId, setInitialMessageId] = useState<string>();
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarCompact, setSidebarCompact] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [globalSearchFocused, setGlobalSearchFocused] = useState(false);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [globalResults, setGlobalResults] = useState<{
    messages: EmailSummary[];
    contacts: EmailContact[];
    templates: EmailTemplate[];
  }>({ messages: [], contacts: [], templates: [] });
  const globalSearchRef = useRef<HTMLInputElement>(null);
  const knownInboxMessageIds = useRef<Set<string> | null>(null);
  const inboxSignature = useRef("");

  const notify = useCallback(
    (tone: "success" | "error" | "info", title: string, message?: string) => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, tone, title, message }]);
      window.setTimeout(
        () => setToasts((current) => current.filter((toast) => toast.id !== id)),
        4_800
      );
    },
    []
  );

  const loadShared = useCallback(async () => {
    try {
      const [dashboardResult, accountResult, templateResult, folderResult] = await Promise.all([
        api.dashboard(),
        api.accounts(),
        api.templates(),
        api.mailFolders()
      ]);
      setDashboard(dashboardResult);
      setAccounts(accountResult);
      setTemplates(templateResult);
      setMailFolders(folderResult);
    } catch (error) {
      notify("error", "Couldn’t refresh the workspace", (error as Error).message);
    }
  }, [notify]);

  useEffect(() => {
    const focusGlobalSearch = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        globalSearchRef.current?.focus();
      }
      if (event.key === "Escape" && document.activeElement === globalSearchRef.current) {
        setGlobalSearch("");
        setGlobalSearchFocused(false);
        globalSearchRef.current?.blur();
      }
    };
    window.addEventListener("keydown", focusGlobalSearch);
    return () => window.removeEventListener("keydown", focusGlobalSearch);
  }, []);

  useEffect(() => {
    const term = globalSearch.trim();
    if (!user || term.length < 2) {
      setGlobalSearchLoading(false);
      setGlobalResults({ messages: [], contacts: [], templates: [] });
      return;
    }

    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setGlobalSearchLoading(true);
      try {
        const [activeMail, archivedMail, contactResults] = await Promise.all([
          api.messages({ folder: "All", archived: false, search: term, page: 1, pageSize: 10, threaded: true }),
          api.messages({ folder: "All", archived: true, search: term, page: 1, pageSize: 10, threaded: true }),
          api.contacts(term)
        ]);
        if (cancelled) return;
        const seenThreads = new Set<string>();
        const messages = [...activeMail.items, ...archivedMail.items]
          .sort((left, right) => Date.parse(right.occurredAt) - Date.parse(left.occurredAt))
          .filter((message) => {
            const key = `${message.emailAccountId}:${message.threadId}`;
            if (seenThreads.has(key)) return false;
            seenThreads.add(key);
            return true;
          })
          .slice(0, 10);
        const normalized = term.toLocaleLowerCase();
        const templateResults = templates.filter((template) =>
          `${template.name} ${template.category} ${template.description} ${template.subjectTemplate}`
            .toLocaleLowerCase()
            .includes(normalized)
        ).slice(0, 8);
        setGlobalResults({ messages, contacts: contactResults.slice(0, 8), templates: templateResults });
      } catch {
        if (!cancelled) setGlobalResults({ messages: [], contacts: [], templates: [] });
      } finally {
        if (!cancelled) setGlobalSearchLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [globalSearch, templates, user]);

  useEffect(() => {
    if (!session.token) {
      setCheckingSession(false);
      return;
    }
    api
      .me()
      .then(setUser)
      .catch(() => {
        session.token = null;
        setUser(null);
      })
      .finally(() => setCheckingSession(false));
  }, []);

  useEffect(() => {
    const unauthorized = () => setUser(null);
    window.addEventListener("email-manager-unauthorized", unauthorized);
    return () => window.removeEventListener("email-manager-unauthorized", unauthorized);
  }, []);

  useEffect(() => {
    if (user && !user.mustChangePassword) loadShared();
  }, [loadShared, user]);

  useEffect(() => {
    if (!user || user.mustChangePassword) {
      knownInboxMessageIds.current = null;
      inboxSignature.current = "";
      return;
    }
    let cancelled = false;
    let inFlight = false;
    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const [result, inboxPage, folders] = await Promise.all([
          api.dashboard(),
          api.messages({
            folder: "Inbox",
            archived: false,
            page: 1,
            pageSize: 50,
            threaded: false
          }),
          api.mailFolders()
        ]);
        if (cancelled) return;
        const inboxMessages = inboxPage.items.filter((message) => message.direction === "Received");
        const nextSignature = `${result.inboxCount}:${inboxMessages.map((message) => message.id).join(",")}`;
        if (knownInboxMessageIds.current === null) {
          knownInboxMessageIds.current = new Set(inboxMessages.map((message) => message.id));
        } else {
          const arrivals = inboxMessages.filter((message) => !knownInboxMessageIds.current!.has(message.id));
          inboxMessages.forEach((message) => knownInboxMessageIds.current!.add(message.id));
          if (arrivals.length > 0) {
            const newest = arrivals[0];
            const title = arrivals.length === 1 ? "New email received" : `${arrivals.length} new emails received`;
            const body = arrivals.length === 1
              ? `${newest.fromAddress}: ${newest.subject}`
              : `Latest: ${newest.subject}`;
            notify("info", title, body);
            void showNewMailNotification(title, body);
          }
        }
        if (inboxSignature.current && inboxSignature.current !== nextSignature)
          setRefreshKey((value) => value + 1);
        inboxSignature.current = nextSignature;
        setDashboard(result);
        setMailFolders(folders);
      } catch {
        // A regular workspace refresh will surface persistent connectivity problems.
      } finally {
        inFlight = false;
      }
    };
    void poll();
    const timer = window.setInterval(poll, WORKSPACE_POLL_MS);
    window.addEventListener("focus", poll);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", poll);
    };
  }, [notify, user]);

  useEffect(() => {
    const unread = dashboard?.unreadCount ?? 0;
    document.title = unread > 0 ? `(${unread}) Unify Mail` : "Unify Mail";
  }, [dashboard?.unreadCount]);

  const navigate = (destination: PageKey) => {
    setPage(destination);
    if (!["inbox", "sent", "drafts", "archive", "spam", "trash", "folder"].includes(destination))
      setInitialMessageId(undefined);
  };

  const navigateFolder = (folderName: string) => {
    setCustomFolder(folderName);
    setInitialMessageId(undefined);
    setPage("folder");
  };

  const openComposer = (seed?: ComposerSeed) => {
    setComposerSeed(seed);
    setComposerOpen(true);
  };

  const handleChanged = async () => {
    setRefreshKey((value) => value + 1);
    await loadShared();
  };

  const openMessage = (message: EmailSummary) => {
    setInitialMessageId(message.id);
    if (message.isDeleted || message.folder === "Trash") setPage("trash");
    else if (message.isArchived) setPage("archive");
    else if (message.folder === "Sent") setPage("sent");
    else if (message.folder === "Spam") setPage("spam");
    else if (message.folder === "Drafts") setPage("drafts");
    else if (message.folder === "Inbox") setPage("inbox");
    else {
      setCustomFolder(message.folder);
      setPage("folder");
    }
  };

  const closeGlobalSearch = () => {
    setGlobalSearch("");
    setGlobalSearchFocused(false);
    globalSearchRef.current?.blur();
  };

  const configureNotifications = async () => {
    const enabled = await requestNotificationAccess();
    notify(
      enabled ? "success" : "info",
      enabled ? "Desktop notifications enabled" : "Notifications are blocked",
      enabled ? "New messages will use your system notification sound." : "Allow notifications in your browser or operating-system settings."
    );
  };

  const logout = async () => {
    try { await api.logout(); } catch { /* local session is still cleared */ }
    session.token = null;
    setUser(null);
    setDashboard(null);
    setAccounts([]);
    setTemplates([]);
    setMailFolders([]);
    setCustomFolder("");
    setGlobalSearch("");
  };

  const unreadCount = dashboard?.unreadCount ?? 0;
  const pageTitle = useMemo(() => {
    const labels: Record<PageKey, string> = {
      dashboard: "Overview",
      inbox: "Inbox",
      sent: "Sent",
      drafts: "Drafts",
      archive: "Archive",
      spam: "Spam",
      trash: "Trash",
      folder: customFolder || "Folder",
      contacts: "Contacts",
      templates: "Templates",
      accounts: "Email accounts",
      team: "People",
      roles: "Roles & access",
      audit: "Audit activity",
      settings: "Settings"
    };
    return labels[page];
  }, [customFolder, page]);

  if (checkingSession) {
    return (
      <main className="splash-screen">
        <span className="brand-mark">
          <Mail size={24} />
        </span>
        <strong>Unify Mail</strong>
        <div className="splash-loader" />
      </main>
    );
  }
  if (!user) return <LoginPage onLogin={setUser} />;

  return (
    <div className={`app-shell ${sidebarCompact ? "sidebar-compact" : ""}`}>
      <header className="desktop-titlebar">
        <div className="titlebar-brand">
          <span className="brand-mark small">
            <Mail size={17} />
          </span>
          <span>Unify Mail</span>
        </div>
        <span className="titlebar-page">{pageTitle}</span>
      </header>

      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="brand-mark">
            <Mail size={21} />
          </span>
          <span className="brand-copy">
            <strong>Unify</strong>
            <small>Team mail</small>
          </span>
          <IconButton
            label="Toggle sidebar"
            onClick={() => setSidebarCompact(!sidebarCompact)}
            className="sidebar-toggle"
          >
            <Menu size={17} />
          </IconButton>
        </div>
        <Button className="sidebar-compose" icon={<Plus size={18} />} onClick={() => openComposer()}>
          <span>New message</span>
        </Button>
        <nav>
          {navGroups.map((group) => {
            if (group.adminOnly && !user.isAdministrator) return null;
            return (
              <div className="nav-group" key={group.label}>
                <span className="nav-label">{group.label}</span>
                {group.items.map((item) => {
                  if (item.adminOnly && !user.isAdministrator) return null;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.page}
                      className={page === item.page ? "active" : ""}
                      onClick={() => navigate(item.page)}
                      title={item.label}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                      {item.page === "inbox" && unreadCount > 0 && (
                        <strong className="nav-count">{unreadCount}</strong>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
          {mailFolders.some((folder) => folder.kind === "custom") && (
            <div className="nav-group">
              <span className="nav-label">Mailbox folders</span>
              {mailFolders.filter((folder) => folder.kind === "custom").map((folder) => (
                <button
                  key={folder.name}
                  className={page === "folder" && customFolder === folder.name ? "active" : ""}
                  onClick={() => navigateFolder(folder.name)}
                  title={folder.displayName}
                >
                  <Folder size={18} />
                  <span>{folder.displayName}</span>
                  {folder.unreadCount > 0 && <strong className="nav-count">{folder.unreadCount}</strong>}
                </button>
              ))}
            </div>
          )}
        </nav>
        <div className="sidebar-foot">
          <div className="secure-indicator">
            <ShieldCheck size={16} />
            <span>
              <strong>Secure workspace</strong>
              <small>Server-side credentials</small>
            </span>
          </div>
          <button className="profile-button" onClick={() => setProfileOpen(!profileOpen)}>
            <UserAvatar user={user} />
            <span>
              <strong>
                {user.firstName} {user.lastName}
              </strong>
              <small>{user.isAdministrator ? "Administrator" : user.roles[0] ?? "Team member"}</small>
            </span>
            <ChevronDown size={15} />
          </button>
          {profileOpen && (
            <div className="profile-menu">
              <button onClick={logout}>Sign out</button>
            </div>
          )}
        </div>
      </aside>

      <header className="app-topbar">
        <div className="global-search-wrap">
          <label className="global-search">
            <Search size={17} />
            <input
              ref={globalSearchRef}
              value={globalSearch}
              aria-label="Search the workspace"
              placeholder="Search mail, people, templates…"
              onChange={(event) => setGlobalSearch(event.target.value)}
              onFocus={() => setGlobalSearchFocused(true)}
              onBlur={() => window.setTimeout(() => setGlobalSearchFocused(false), 120)}
            />
            {globalSearch ? (
              <button
                type="button"
                className="global-search-clear"
                aria-label="Clear workspace search"
                onMouseDown={(event) => event.preventDefault()}
                onClick={closeGlobalSearch}
              >
                ×
              </button>
            ) : <kbd>Ctrl K</kbd>}
          </label>
          {globalSearchFocused && globalSearch.trim().length >= 2 && (
            <section className="global-search-results" aria-label="Workspace search results">
              {globalSearchLoading ? (
                <span className="global-search-status">Searching workspace…</span>
              ) : globalResults.messages.length + globalResults.contacts.length + globalResults.templates.length === 0 ? (
                <span className="global-search-status">No matching mail, contacts, or templates.</span>
              ) : (
                <>
                  {globalResults.messages.length > 0 && (
                    <div>
                      <h3>Mail</h3>
                      {globalResults.messages.map((message) => (
                        <button
                          key={message.id}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => { closeGlobalSearch(); openMessage(message); }}
                        >
                          <Mail size={15} />
                          <span>
                            <strong>{message.subject}</strong>
                            <small>{senderName(message.fromAddress)} · {message.accountName}</small>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {globalResults.contacts.length > 0 && (
                    <div>
                      <h3>People</h3>
                      {globalResults.contacts.map((contact) => (
                        <button
                          key={contact.id ?? contact.emailAddress}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            closeGlobalSearch();
                            openComposer({
                              to: contact.name
                                ? `${contact.name} <${contact.emailAddress}>`
                                : contact.emailAddress
                            });
                          }}
                        >
                          <ContactRound size={15} />
                          <span><strong>{contact.name}</strong><small>{contact.emailAddress}</small></span>
                        </button>
                      ))}
                    </div>
                  )}
                  {globalResults.templates.length > 0 && (
                    <div>
                      <h3>Templates</h3>
                      {globalResults.templates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => { closeGlobalSearch(); openComposer({ templateId: template.id }); }}
                        >
                          <FileText size={15} />
                          <span><strong>{template.name}</strong><small>{template.category}</small></span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </section>
          )}
        </div>
        <div className="topbar-actions">
          <Badge tone="success">
            <span className="status-dot" /> All systems ready
          </Badge>
          <IconButton label="Notifications" className="notification-button" onClick={configureNotifications}>
            <Bell size={18} />
            {unreadCount > 0 && <span>{Math.min(unreadCount, 9)}</span>}
          </IconButton>
          <span className="topbar-divider" />
          <button className="topbar-profile" onClick={() => setProfileOpen(!profileOpen)}>
            <UserAvatar user={user} size="sm" />
            <span>{user.firstName}</span>
            <ChevronDown size={14} />
          </button>
        </div>
      </header>

      <main className={`app-content ${["inbox", "sent", "drafts", "archive", "spam", "trash", "folder"].includes(page) ? "mail-content" : ""}`}>
        {page === "dashboard" && (
          <DashboardPage
            data={dashboard}
            user={user}
            onCompose={() => openComposer()}
            navigate={navigate}
            openMessage={openMessage}
          />
        )}
        {page === "inbox" && (
          <MailPage
            mode="Inbox"
            accounts={accounts}
            initialMessageId={initialMessageId}
            onCompose={openComposer}
            notify={notify}
            refreshKey={refreshKey}
            canViewViewerHistory={user.isAdministrator}
          />
        )}
        {page === "sent" && (
          <MailPage
            mode="Sent"
            accounts={accounts}
            initialMessageId={initialMessageId}
            onCompose={openComposer}
            notify={notify}
            refreshKey={refreshKey}
            canViewViewerHistory={user.isAdministrator}
          />
        )}
        {page === "drafts" && (
          <MailPage
            mode="Drafts"
            accounts={accounts}
            initialMessageId={initialMessageId}
            onCompose={openComposer}
            notify={notify}
            refreshKey={refreshKey}
            canViewViewerHistory={user.isAdministrator}
          />
        )}
        {page === "archive" && (
          <MailPage
            mode="Archive"
            accounts={accounts}
            initialMessageId={initialMessageId}
            onCompose={openComposer}
            notify={notify}
            refreshKey={refreshKey}
            canViewViewerHistory={user.isAdministrator}
          />
        )}
        {page === "spam" && (
          <MailPage
            mode="Spam"
            accounts={accounts}
            initialMessageId={initialMessageId}
            onCompose={openComposer}
            notify={notify}
            refreshKey={refreshKey}
            canViewViewerHistory={user.isAdministrator}
          />
        )}
        {page === "trash" && (
          <MailPage
            mode="Trash"
            accounts={accounts}
            initialMessageId={initialMessageId}
            onCompose={openComposer}
            notify={notify}
            refreshKey={refreshKey}
            canViewViewerHistory={user.isAdministrator}
          />
        )}
        {page === "folder" && (
          <MailPage
            mode={customFolder}
            accounts={accounts}
            initialMessageId={initialMessageId}
            onCompose={openComposer}
            notify={notify}
            refreshKey={refreshKey}
            canViewViewerHistory={user.isAdministrator}
          />
        )}
        {page === "contacts" && <ContactsPage user={user} notify={notify} />}
        {page === "templates" && (
          <TemplatesPage
            user={user}
            onCompose={openComposer}
            notify={notify}
            onChanged={handleChanged}
          />
        )}
        {page === "accounts" && (
          <AccountsPage user={user} notify={notify} onChanged={handleChanged} />
        )}
        {page === "team" && <TeamPage mode="users" notify={notify} />}
        {page === "roles" && <TeamPage mode="roles" notify={notify} />}
        {page === "audit" && <AuditPage notify={notify} />}
        {page === "settings" && <SettingsPage notify={notify} />}
      </main>

      <Composer
        open={composerOpen}
        accounts={accounts}
        templates={templates}
        seed={composerSeed}
        onClose={() => setComposerOpen(false)}
        onSent={handleChanged}
        notify={notify}
      />
      {user.mustChangePassword && (
        <PasswordChange user={user} onChanged={setUser} notify={notify} />
      )}
      <ToastStack
        toasts={toasts}
        dismiss={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))}
      />
    </div>
  );
}


